import { useState } from 'react';
import { Link, useNavigate, useParams } from 'react-router-dom';
import { Badge, Button, Card, CardHeader, ErrorState, PageHeader, PageLoader, useToast } from '../../../components/ui';
import { useCrumb } from '../../../components/layout/Breadcrumbs/Breadcrumbs';
import { KeyValues, ReasonDialog, TwoColumn } from '../../../components/common/Parts/Parts';
import { useAction } from '../../../hooks/useAction';
import { useFetch } from '../../../hooks/useFetch';
import { labelOf } from '../../../utils/labels';
import { PAYMENT_MODES } from '../../../constants/payments';
import { masterLabel } from '../../masters/services/masterService';
import { useMasters } from '../../masters/hooks/useMasters';
import { formatDate } from '../../../utils/date';
import { money } from '../../../utils/currency';
import { viewUrl } from '../../../services/api/uploadService';
import { expenseService } from '../services/expenseService';

export default function ExpenseDetails() {
  const { id = '' } = useParams();
  const navigate = useNavigate();
  const toast = useToast();
  const { busy, run } = useAction(toast);
  const [cancelling, setCancelling] = useState(false);
  useMasters();
  const { data: e, error, reload } = useFetch(() => expenseService.get(id), [id]);
  useCrumb(`/expenses/${id}`, e?.expenseNo);

  if (error) return <ErrorState error={error} onRetry={reload} />;
  if (!e) return <PageLoader />;

  const active = e.status === 'active';
  const cancel = async (reason) => {
    const res = await run('cancel', () => expenseService.cancel(id, reason), 'Expense cancelled; its payment was reversed');
    setCancelling(false);
    if (res) reload();
  };
  const openBill = () => e.billPhotoUrl && run('bill', async () => window.open(await viewUrl(e.billPhotoUrl as string), '_blank', 'noopener'));

  return (
    <>
      <PageHeader
        title={e.expenseNo}
        badge={active ? <Badge tone="green" dot>Active</Badge> : <Badge tone="red" dot>Cancelled</Badge>}
        subtitle={`${masterLabel('expenseCategory', e.category)} · ${formatDate(e.date)}`}
        actions={
          <>
            {active && <Button variant="dangerText" onClick={() => setCancelling(true)}>Cancel expense</Button>}
            {e.billPhotoUrl && <Button variant="outline" leftIcon="eye" loading={busy === 'bill'} onClick={openBill}>View bill</Button>}
            {active && <Button variant="outline" leftIcon="edit" onClick={() => navigate(`/expenses/${id}/edit`)}>Edit</Button>}
          </>
        }
      />
      {!active && <p className="mb-5 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-700">Cancelled: {e.cancelReason}</p>}
      <TwoColumn
        main={
          <Card>
            <CardHeader title="Expense details" />
            <div className="p-5 pt-4">
              <KeyValues
                rows={[
                  ['Category', masterLabel('expenseCategory', e.category)],
                  ['Date', formatDate(e.date)],
                  ['Paid to', e.vendorName || '-'],
                  ['Bill number', e.billNo || '-'],
                  e.vendorGstin && ['Their GSTIN', e.vendorGstin],
                  ['Paid by', labelOf(PAYMENT_MODES, e.paymentMode)],
                  e.serviceId && ['For job', <Link to={`/jobs/${e.serviceId}`} className="text-brand-600 hover:underline">Open job</Link>],
                  e.transactionId && ['Payment entry', <Link to={`/transactions/${e.transactionId}`} className="text-brand-600 hover:underline">Open payment</Link>],
                  e.notes && ['Notes', e.notes],
                ]}
              />
            </div>
          </Card>
        }
        aside={
          <Card>
            <CardHeader title="Amount" />
            <div className="p-5 pt-4">
              <KeyValues rows={[['Before GST', money(e.amount - (e.gstAmount ?? 0))], ['GST', money(e.gstAmount)], ['Total paid', money(e.amount), { strong: true }]]} />
            </div>
          </Card>
        }
      />
      <ReasonDialog
        open={cancelling}
        onClose={() => setCancelling(false)}
        onConfirm={cancel}
        loading={busy === 'cancel'}
        title="Cancel this expense?"
        message="The money-out entry is reversed automatically. The expense stays in the list as cancelled."
        confirmLabel="Cancel expense"
      />
    </>
  );
}
